/**
 * canonicalDefinitions (AJIP-16.02.005 · WS4)
 * ---------------------------------------------------------------------
 * Enriched 25-field superset of the six canonical journeys. Founder
 * Ruling RD-02: this becomes the single canonical source of truth. The
 * legacy `registry/canonicalJourneys.js` (from AJIP-16.02.003) is
 * transitional and will be retired in a future cleanup package.
 *
 * These are initial registrations only, NOT architectural limits.
 */
import { STATES } from '../core/JourneyState';

const NOW = '2026-02-11T00:00:00.000Z';

const baseTransitions = () => [
  { from: STATES.STARTED,     event: 'proceed',  to: STATES.IN_PROGRESS },
  { from: STATES.IN_PROGRESS, event: 'wait',     to: STATES.WAITING     },
  { from: STATES.WAITING,     event: 'resume',   to: STATES.IN_PROGRESS },
  { from: STATES.IN_PROGRESS, event: 'complete', to: STATES.COMPLETED   },
  { from: STATES.IN_PROGRESS, event: 'cancel',   to: STATES.CANCELLED   },
  { from: STATES.IN_PROGRESS, event: 'fail',     to: STATES.FAILED      },
];

const makeDefinition = ({ id, name, description, type, category, audience, priority, tags, canonRef }) => ({
  journeyId: id,
  journeyVersion: 1,
  journeyName: name,
  journeyDescription: description,
  journeyType: type,
  journeyCategory: category,
  journeyAudience: audience,
  registrationStatus: 'registered',
  requiredFeatureFlags: ['JOURNEYS_ENABLED'],
  entryConditions: [],
  eligibilityRules: [],
  permissionRequirements: [],
  initialState: STATES.NOT_STARTED,
  supportedStates: [
    STATES.NOT_STARTED, STATES.STARTED, STATES.IN_PROGRESS,
    STATES.WAITING, STATES.BLOCKED, STATES.COMPLETED,
    STATES.CANCELLED, STATES.FAILED,
  ],
  transitionConfiguration: baseTransitions(),
  persistencePolicyRef: null,
  retentionPolicyRef: null,
  priority,
  tags,
  canonReference: canonRef,
  owner: 'Founder',
  createdAt: NOW,
  updatedAt: NOW,
  deprecationStatus: 'none',
  replacementJourneyId: null,
});

const CANONICAL_DEFINITIONS_V2 = Object.freeze([
  makeDefinition({
    id: 'founder', name: 'Founder Journey',
    description: 'Founder discovery, validation and onboarding pathway.',
    type: 'founder', category: 'onboarding',
    audience: ['authorised-founder'], priority: 10, tags: ['founder', 'onboarding'],
    canonRef: 'AJIP-16.02.005',
  }),
  makeDefinition({
    id: 'buyer', name: 'Buyer Journey',
    description: 'Buyer discovery, comparison, RFQ and decision flows.',
    type: 'buyer', category: 'sourcing',
    audience: ['authenticated', 'anonymous', 'authorised-buyer'], priority: 20, tags: ['buyer', 'sourcing'],
    canonRef: 'AJIP-16.02.005',
  }),
  makeDefinition({
    id: 'supplier', name: 'Supplier Journey',
    description: 'Supplier claim, verification, quote and profile flows.',
    type: 'supplier', category: 'verification',
    audience: ['authorised-supplier'], priority: 30, tags: ['supplier', 'verification'],
    canonRef: 'AJIP-16.02.005',
  }),
  makeDefinition({
    id: 'factory', name: 'Factory Journey',
    description: 'Factory-side capacity, verification and production readiness.',
    type: 'factory', category: 'operational',
    audience: ['authorised-factory'], priority: 40, tags: ['factory', 'operational'],
    canonRef: 'AJIP-16.02.005',
  }),
  makeDefinition({
    id: 'brand', name: 'Brand Journey',
    description: 'Brand discovery, brand-to-factory intelligence, and sourcing engagement.',
    type: 'brand', category: 'sourcing',
    audience: ['authorised-brand'], priority: 50, tags: ['brand', 'sourcing'],
    canonRef: 'AJIP-16.02.005',
  }),
  makeDefinition({
    id: 'administrator', name: 'Administrator Journey',
    description: 'Administrator moderation, verification review, and platform operations.',
    type: 'administrator', category: 'moderation',
    audience: ['authorised-administrator'], priority: 60, tags: ['administrator', 'moderation'],
    canonRef: 'AJIP-16.02.005',
  }),
]);

const registerCanonicalV2 = (registrationService) => {
  const registered = [];
  for (const def of CANONICAL_DEFINITIONS_V2) {
    if (!registrationService.getRegistrationStatus(def.journeyId)) {
      registrationService.register(def);
      registered.push(def.journeyId);
    }
  }
  return Object.freeze(registered);
};

export { CANONICAL_DEFINITIONS_V2, registerCanonicalV2 };
